import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, formatError } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import { Star, Mail, Lock, User, Eye, EyeOff, Phone } from "lucide-react";

const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,24}$/;
const PHONE_RE = /^[+\d\s().-]{8,20}$/;
const digitsOf = (v) => (v || "").replace(/\D/g, "");

export default function AuthPage() {
  const location = useLocation();
  const { t: tr } = useI18n();
  const [tab, setTab] = useState(new URLSearchParams(location.search).get("tab") === "login" ? "login" : "signup");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login, register, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      const redirectTo = new URLSearchParams(location.search).get("redirect") || "/dashboard";
      navigate(redirectTo, { replace: true });
    }
  }, [user, navigate, location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    // ONE field for both tabs: it holds either an email address or a phone number.
    const contact = (email || "").trim();
    const isPhone = !!contact && !contact.includes("@");
    setLoading(true);
    try {
      if (!contact) {
        setError("Enter your email address or phone number"); setLoading(false); return;
      }
      if (isPhone) {
        if (!PHONE_RE.test(contact) || digitsOf(contact).length < 10) {
          setError("Enter a 10-digit mobile number, or use your email address"); setLoading(false); return;
        }
      } else if (contact.length > 254 || !EMAIL_RE.test(contact) || contact.includes("..")) {
        setError("Please enter a valid email address (e.g. you@gmail.com)"); setLoading(false); return;
      }
      if (tab === "login") {
        await login(contact, password);
      } else {
        if (!name.trim()) { setError("Name is required"); setLoading(false); return; }
        await register(name, isPhone ? "" : contact, password, isPhone ? contact : "");
      }
      const redirectTo = new URLSearchParams(location.search).get("redirect") || "/dashboard";
      navigate(redirectTo);
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#120021", display: "flex", alignItems: "center", justifyContent: "center", padding: "80px 16px 40px" }}>
      {/* Background nebula */}
      <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", pointerEvents: "none", overflow: "hidden" }}>
        <div style={{ position: "absolute", width: "600px", height: "600px", background: "radial-gradient(circle, #2D0B45 0%, transparent 70%)", top: "-200px", right: "-200px", opacity: 0.5 }} />
        <div style={{ position: "absolute", width: "500px", height: "500px", background: "radial-gradient(circle, #5B1E8C 0%, transparent 70%)", bottom: "-200px", left: "-200px", opacity: 0.3 }} />
      </div>

      <div className="animate-fadeInUp" style={{ width: "100%", maxWidth: "420px", position: "relative", zIndex: 10 }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <img
            src="/logo-mark.png"
            alt="AstroFate"
            style={{ width: "76px", height: "76px", objectFit: "contain", marginBottom: "12px", filter: "drop-shadow(0 0 26px rgba(255,209,102,0.35))" }}
          />
          <h1 className="font-cinzel gradient-text-gold" style={{ fontSize: "2rem", fontWeight: "700" }}>AstroFate</h1>
          <p style={{ color: "rgba(255,255,255,0.5)", marginTop: "8px", fontSize: "0.9rem" }}>
            {tab === "login" ? tr("auth.welcomeBack") : tr("auth.createAccount")}
          </p>
        </div>

        {/* Card */}
        <div className="glass-card" style={{ padding: "32px" }}>
          {/* Tabs */}
          <div style={{ display: "flex", background: "rgba(255,255,255,0.05)", borderRadius: "50px", padding: "4px", marginBottom: "28px" }}>
            {["login", "signup"].map(tabKey => (
              <button
                key={tabKey}
                data-testid={`auth-tab-${tabKey}`}
                onClick={() => { setTab(tabKey); setError(""); }}
                style={{
                  flex: 1, padding: "10px", border: "none", cursor: "pointer", borderRadius: "50px",
                  background: tab === tabKey ? "linear-gradient(135deg, #F4B942, #FFD166)" : "transparent",
                  color: tab === tabKey ? "#120021" : "rgba(255,255,255,0.6)",
                  fontWeight: tab === tabKey ? "700" : "500",
                  fontFamily: "'DM Sans', sans-serif", fontSize: "0.9rem",
                  transition: "all 0.3s ease"
                }}
              >
                {tabKey === "login" ? tr("auth.tabLogin") : tr("auth.tabSignup")}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit}>
            {tab === "signup" && (
              <div data-testid="auth-signup-gift" style={{ display: "flex", alignItems: "center", gap: "8px", background: "rgba(255,209,102,0.08)", border: "1px solid rgba(255,209,102,0.25)", borderRadius: "10px", padding: "9px 14px", marginBottom: "18px" }}>
                <span style={{ color: "#FFD166", fontSize: "0.95rem" }}>✦</span>
                <span style={{ color: "#FFD166", fontSize: "0.8rem", fontWeight: 600 }}>
                  {(new URLSearchParams(location.search).get("redirect") || "").includes("autosend=1") ? tr("auth.questionSaved") : tr("promo.signupGift")}
                </span>
              </div>
            )}
            {tab === "signup" && (
              <div style={{ marginBottom: "16px" }}>
                <label style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.8rem", display: "block", marginBottom: "6px" }}>{tr("auth.nameLabel")}</label>
                <div style={{ position: "relative" }}>
                  <User size={16} style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.3)" }} />
                  <input data-testid="auth-name-input" type="text" value={name} onChange={e => setName(e.target.value)} placeholder={tr("auth.namePh")} className="cosmic-input" style={{ paddingLeft: "40px" }} required />
                </div>
              </div>
            )}
            <div style={{ marginBottom: "16px" }}>
              <label style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.8rem", display: "block", marginBottom: "6px" }}>
                Email or phone number
              </label>
              <div style={{ position: "relative" }}>
                {(email || "").includes("@") || !(email || "").trim()
                  ? <Mail size={16} style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.3)" }} />
                  : <Phone size={16} style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.3)" }} />}
                <input data-testid="auth-email-input" type="text" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@gmail.com or 98765 43210"
                  autoComplete={tab === "login" ? "username" : "email"}
                  className="cosmic-input" style={{ paddingLeft: "40px" }} required />
              </div>
              {tab === "signup" && (
                <p style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.72rem", marginTop: "6px" }}>
                  Use either one — you&apos;ll sign in with the same. Indian numbers don&apos;t need +91.
                </p>
              )}
            </div>
            <div style={{ marginBottom: "24px" }}>
              <label style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.8rem", display: "block", marginBottom: "6px" }}>{tr("auth.passwordLabel")}</label>
              <div style={{ position: "relative" }}>
                <Lock size={16} style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.3)" }} />
                <input data-testid="auth-password-input" type={showPw ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} placeholder={tr("auth.passwordPh")} className="cosmic-input" style={{ paddingLeft: "40px", paddingRight: "44px" }} required minLength={6} />
                <button type="button" onClick={() => setShowPw(!showPw)} style={{ position: "absolute", right: "14px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.4)" }}>
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {tab === "login" && (
                <div style={{ textAlign: "right", marginTop: "8px" }}>
                  <button type="button" data-testid="auth-forgot-link" onClick={() => navigate("/auth/forgot")}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#FFD166", fontSize: "0.8rem", fontWeight: 500 }}>
                    {tr("auth.forgot")}
                  </button>
                </div>
              )}
            </div>

            {error && (
              <div data-testid="auth-error" style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: "8px", padding: "10px 14px", marginBottom: "16px", color: "#fca5a5", fontSize: "0.85rem" }}>
                {error}
              </div>
            )}

            <button data-testid="auth-submit-btn" type="submit" disabled={loading} className="btn-gold" style={{ width: "100%", opacity: loading ? 0.7 : 1 }}>
              {loading ? "…" : tab === "login" ? tr("auth.submitLogin") : tr("auth.submitSignup")}
            </button>
          </form>

          {/* Divider */}
          <div style={{ display: "flex", alignItems: "center", gap: "12px", margin: "20px 0" }}>
            <div style={{ flex: 1, height: "1px", background: "rgba(255,255,255,0.1)" }} />
            <span style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.78rem" }}>{tr("auth.or")}</span>
            <div style={{ flex: 1, height: "1px", background: "rgba(255,255,255,0.1)" }} />
          </div>

          {/* Emergent managed Google sign-in */}
          <button
            type="button"
            data-testid="auth-google-btn"
            onClick={() => {
              // Remember where to return after Google sign-in (e.g. the chat with the typed question).
              const back = new URLSearchParams(location.search).get("redirect");
              if (back && back.startsWith("/")) sessionStorage.setItem("astrofate_auth_redirect", back);
              else sessionStorage.removeItem("astrofate_auth_redirect");
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/dashboard";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            style={{
              width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px",
              background: "white", color: "#1f2937", border: "none", borderRadius: "50px",
              padding: "11px 20px", fontWeight: 600, fontSize: "0.9rem", cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif"
            }}
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            {tr("auth.googleBtn")}
          </button>

          <p style={{ textAlign: "center", marginTop: "20px", color: "rgba(255,255,255,0.4)", fontSize: "0.8rem" }}>
            <button onClick={() => { setTab(tab === "login" ? "signup" : "login"); setError(""); }}
              style={{ background: "none", border: "none", cursor: "pointer", color: "#FFD166", fontWeight: "600" }}>
              {tab === "login" ? tr("auth.switchToSignup") : tr("auth.switchToLogin")}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
